@import '../../utils/control-density.css';
@import '../../utils/control-parts.css';
@import '../../utils/chrome-layout.css';
@import '../../utils/focus-ring.css';
@import '../../utils/control-inactive.css';

:host {
  display: block;
}

.bar-nav {
  position: relative;
  height: var(--dimension-size-600);
  border-bottom: var(--dimension-stroke-width-012) solid var(--_bar-nav-border);
  background-color: var(--_bar-nav-bg);
  color: var(--_bar-nav-fg-primary);
  -webkit-user-select: none;
  user-select: none;
  overflow: hidden;
  transition:
    background-color var(--effect-motion-short-3),
    border-color var(--effect-motion-short-3),
    color var(--effect-motion-short-3);
  isolation: isolate;
  /* Default chrome — app surfaces */
  --_bar-nav-bg: var(--color-background-secondary);
  --_bar-nav-border: var(--color-border-tertiary);
  --_bar-nav-fg-primary: var(--color-foreground-primary);
  --_bar-nav-fg-secondary: var(--color-foreground-secondary);
  --_bar-nav-hover: var(--color-interaction-hover);
  --_bar-nav-pressed: var(--color-interaction-pressed);
  --_bar-nav-focus: var(--color-interaction-focus);
  --_bar-nav-dot: var(--color-foreground-bold-brand);
  --ds-focus-ring-color: var(--_bar-nav-focus);
}

/* Style slots — same app tokens for now; hook texture/glyph layers later. */
.bar-nav--dashboard,
.bar-nav--settings {
  --_bar-nav-bg: var(--color-background-secondary);
  --_bar-nav-border: var(--color-border-tertiary);
  --_bar-nav-fg-primary: var(--color-foreground-primary);
  --_bar-nav-fg-secondary: var(--color-foreground-secondary);
  --_bar-nav-hover: var(--color-interaction-hover);
  --_bar-nav-pressed: var(--color-interaction-pressed);
  --_bar-nav-focus: var(--color-interaction-focus);
  --_bar-nav-dot: var(--color-foreground-bold-brand);
}

/* Shell-owned chrome (bg + wash) — bar is a transparent frame. */
:host-context(ds-shell-app:not([gradient-preset='none'])) .bar-nav,
:host-context(ds-shell-app.shell-app--paper-texture) .bar-nav {
  background-color: transparent;
}

.bar-nav > :not(.bar-nav__tabs-probe) {
  position: relative;
  z-index: 3;
}

/* Overflow — keep fitting tabs visible and pin the overflow trigger to the far edge. */
.bar-nav--tabs-collapsed {
  display: flex;
  gap: var(--dimension-space-100);
  align-items: center;
}

/* Expanded tabs — consume full header width */
.bar-nav__left {
  position: relative;
  flex: 1 1 0%;
  min-width: 0;
  width: 0;
  display: flex;
  align-items: center;
}

.bar-nav__tabs-visible {
  flex: 1;
  min-width: 0;
  width: 100%;
}

/* Unconstrained width so intrinsic tab row width is measurable while collapsed */
.bar-nav__tabs-probe {
  position: absolute;
  left: 0;
  top: 0;
  visibility: hidden;
  pointer-events: none;
  overflow: visible;
  width: max-content;
  max-width: none;
  z-index: -1;
}

.bar-nav__tabs-probe > div {
  width: max-content;
}

.bar-nav__tab-list {
  display: flex;
  align-items: center;
  gap: var(--dimension-space-050);
  position: relative;
  width: max-content;
}

.bar-nav__tab {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--ds-control-gap);
  height: var(--ds-control-height);
  min-width: var(--ds-control-height);
  padding: 0 var(--ds-control-padding-inline);
  box-sizing: border-box;
  border: none;
  border-radius: var(--ds-control-radius);
  background-color: transparent;
  color: var(--_bar-nav-fg-secondary);
  cursor: default;
  position: relative;
  flex-shrink: 0;
  -webkit-user-select: none;
  user-select: none;
  transition: color var(--effect-motion-short-2);
}

.bar-nav__tab::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: var(--ds-control-radius);
  background: transparent;
  pointer-events: none;
  cursor: inherit;
}

@media (hover: hover) and (pointer: fine) {
  .bar-nav__tab:hover::after {
    background: var(--_bar-nav-hover);
  }
}

.bar-nav__tab:active::after {
  background: var(--_bar-nav-pressed);
}

.bar-nav__tab--selected {
  color: var(--_bar-nav-fg-primary);
}

.bar-nav__tab-label {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

.bar-nav__tab-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: var(--dimension-space-100);
  height: var(--ds-control-height);
  padding-block: var(--ds-control-padding-inline);
  flex-shrink: 0;
  pointer-events: none;
}

.bar-nav__tab-divider-line {
  width: var(--dimension-stroke-width-012);
  align-self: stretch;
  background-color: var(--_bar-nav-border);
}

/* In-flow placeholder while measuring overflow — avoids layout jump; hidden from view/accessibility. */
.bar-nav__tabs-pending {
  flex: 1;
  min-width: 0;
  height: var(--ds-control-height);
  visibility: hidden;
  pointer-events: none;
}

/* Overflow trigger — compact icon button aligned to the right edge. */
.bar-nav__overflow-trigger {
  margin-left: auto;
  --ds-button-unfilled-fg: var(--_bar-nav-fg-secondary);
  --ds-button-unfilled-fg-active: var(--_bar-nav-fg-primary);
  --ds-interaction-hover: var(--_bar-nav-hover);
  --ds-interaction-pressed: var(--_bar-nav-pressed);
  --ds-focus-ring-color: var(--_bar-nav-focus);
}

/* Fallback heading when no tabs — matches a single (selected) tab so the area
   name shares the tab label's type token, color, height and horizontal inset.
   Inset = tab button padding (space-075, 6px) + label inset (space-025, 2px).
   Color comes from ds-text color="primary"; beat Text's inline-box display. */
ds-text.bar-nav__heading {
  display: inline-flex;
  align-items: center;
  height: var(--ds-control-height);
  padding-inline: calc(var(--ds-control-padding-inline) + var(--ds-control-label-inset));
  -webkit-user-select: none;
  user-select: none;
}
